import { useToast } from "@expert/common-ui";
import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import type { GlobalConfig } from "@simplrsupport/headless-chat-web";
import { useChatSessionStateStore, useSocketStateStore } from "@simplrsupport/headless-chat-web";
import { useEffect, useMemo } from "react";
import { GuidedFlowMessageMapper, useSimplrInitializers } from "../logic-builder";
import { useGuideStore } from "../store";
import { useGuideDisclosure } from "./useGuideDisclosure";
import { useParseMessages } from "./useParseMessages";

export interface GuideManagerProps {
    // Session/task
    sessionId: string;
    callSid?: string;
    employeeId?: string;
    isCustomerSession?: boolean;

    // Simplr config
    globalConfig?: Pick<GlobalConfig, "customFlowSystemId" | "publicServerDomain" | "widgetId">;

    // Other
    analytics?: Record<string, unknown>;
    clientChannelId?: string;
    isSimplrGuideEnabled: boolean;
    logger: Logger;
    partner: Partner;
    requiresTopPadding: boolean;
    sendMessage: SendExpertAssistMessage;
    ssoAccessToken: string;
}

interface GuideToolboxCommand {
    open: () => void;
    tooltip: string;
    quickActionLabel: string;
    disabled: boolean;
    label: string;
}

export function useGuideManager({
    callSid,
    clientChannelId,
    employeeId,
    globalConfig,
    isSimplrGuideEnabled,
    logger: loggerProp,
    partner,
    requiresTopPadding,
    sendMessage,
    sessionId,
    ssoAccessToken,
}: GuideManagerProps) {
    const { platformTicketId } = useChatSessionStateStore();

    const logger = useMemo(() => loggerProp.child({ module: "useGuideManager" }), [loggerProp]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        logger.info({ sessionId, platformTicketId }, "Solve Guide | Session Updated");
    }, [sessionId, platformTicketId]);

    useSimplrInitializers({
        globalConfig,
        isSimplrGuideEnabled,
        logger,
        sessionId,
    });

    const { connectionState } = useSocketStateStore();
    const toast = useToast();

    const {
        analyticsInfo: { currentStepId },
        isLoadingSimplrSession,
        setErrorStateSessionId,
        setIsErrorState,
        setIsLoadingSimplrSession,
    } = useGuideStore();

    const { latestMessage, messages } = useParseMessages({ employeeId });
    const { isGuideOpen, openGuide, closeGuide } = useGuideDisclosure({
        callSid,
        logger,
        platformTicketId,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (connectionState === "ticketEnded") {
            logger.info("Solve Guide | Ticket Ended event received");
            setIsErrorState(true);
            setErrorStateSessionId(sessionId);
        } else setIsErrorState(false);
    }, [connectionState, setErrorStateSessionId, setIsErrorState]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (platformTicketId && currentStepId !== "" && isLoadingSimplrSession === true) {
            logger.info(
                { currentStepId, platformTicketId },
                "Solve Guide | platformTicketId updated and first step loaded, turning off loading state",
            );
            setIsLoadingSimplrSession(false);
        }
    }, [currentStepId, isLoadingSimplrSession, platformTicketId, setIsLoadingSimplrSession]);

    const ActiveGuide = GuidedFlowMessageMapper({
        callSid,
        clientChannelId,
        latestMessage,
        logger,
        partner,
        onGuideClose: closeGuide,
        opened: isGuideOpen,
        requiresTopPadding,
        sendMessage,
        sessionId,
        ssoAccessToken,
    });

    const handleOpenGuideFailure = () => {
        logger.error(
            { connectionState, globalConfig, messages: JSON.stringify(messages) },
            "Solve guide | No active guide found, trigger error toast",
        );
        toast.error("We’re having trouble launching the guide—try again.");
    };

    return {
        guide: ActiveGuide,
        guideToolboxCommand: {
            open: ActiveGuide ? openGuide : handleOpenGuideFailure,
            tooltip: !isSimplrGuideEnabled ? "Coming soon" : undefined,
            quickActionLabel: isSimplrGuideEnabled ? "/g" : undefined,
            disabled: !isSimplrGuideEnabled,
        } as GuideToolboxCommand,
        isGuideOpen,
        closeGuide,
        openGuide,
    };
}
